import { GameCardFrame, OrnamentalRule } from '../../../components/index.js';
import type { GameCardData } from '../build-game-card-data.js';
import { GameCardHeader } from './GameCardHeader.js';
import { GameStatGrid } from './GameStatGrid.js';
import { GameStreakStats } from './GameStreakStats.js';

/**
 * The one card that renders every game's stats in the profile Gameplay
 * section (APP_SPEC §13.3). Word Rush and Secret Number are NOT designed
 * separately — their content differs, their structure does not:
 *
 *   ╔══════ dark frame ══════╗
 *   ║ illustrated header      ║   emblem + game name over the game's art
 *   ║ ┌── parchment inset ──┐ ║
 *   ║ │ primary stats       │ ║   0–N cells, however many the game declares
 *   ║ ├─────────────────────┤ ║
 *   ║ │ current  │ best     │ ║   streak row, same panel
 *   ║ └─────────────────────┘ ║
 *   ║      ◇——◆——◇           ║   dark footing
 *   ╚═════════════════════════╝
 *
 * The card body is the dark frame, never parchment: the parchment is an inset
 * panel *inside* it, with the frame visible as a deliberate margin around the
 * banner and as a footing beneath the panel. That layering —
 * a graded dark shell behind one solid gold edge, the illustrated header,
 * then recessed parchment — is what makes it read as a fantasy game panel
 * rather than a web card with a picture at the top. The edge is deliberately
 * a single line: a second line inset behind it just reads as a gap.
 *
 * A future game needs only a `gameStatistics` row + registered art, never a
 * new component.
 */
export interface GameProfileStatsCardProps {
  game: GameCardData;
  headingLevel?: 2 | 3;
}

export function GameProfileStatsCard({ game, headingLevel = 3 }: GameProfileStatsCardProps) {
  return (
    <GameCardFrame as="article" data-testid={`profile-game-card-${game.id}`} className="min-w-0">
      <div
        // No padding of its own: the banner runs full bleed to the gold trim,
        // so the game's artwork is framed by the trim rather than sitting in a
        // green mat. `overflow-hidden` is what rounds the banner's corners into
        // the frame. Only the parchment panel below is inset, and the footing
        // beneath it is where the dark shell is meant to read.
        //
        // Height is content, not `h-full`: banner + however many stat rows the
        // game declares + streak row + footing. A card carrying two metrics is
        // shorter than one carrying six, and `GameplaySection` lays the pair
        // out `items-start` so it stays shorter rather than inheriting its
        // neighbour's height and paying for it in blank parchment.
        className="relative flex min-w-0 flex-col overflow-hidden rounded-md bg-[linear-gradient(178deg,var(--px-color-frame-elevated,#0c4a40)_0%,var(--px-color-frame,#073832)_46%,#04231e_100%)] shadow-3"
      >
        <GameCardHeader name={game.name} art={game.art} headingLevel={headingLevel} />

        {/* Inset parchment panel: primary stats and the streak row are one
          designed block, recessed into the dark frame and lifted so it laps
          the bottom of the banner, with a gold diamond seated on the seam.
          This is the one element that keeps a margin of dark shell. */}
        {/* The panel is paper, not a flat cream rectangle: the `parchment`
          token as its base, the app's shared paper grain multiplied over it at
          a low dose, and a faint warm inset shading so the sheet darkens
          slightly toward its own edges. Same asset and same material as every
          other parchment surface in the app (`styles/index.css`), which is why
          it is reused rather than reinvented here. `isolate` scopes the
          grain's blend to this panel, and the grain sits at `-z-10` so it
          covers the panel's background and nothing else — every stat above it
          renders at full opacity and full contrast. */}
        <div className="relative isolate z-[3] mx-2.5 -mt-2 flex flex-col overflow-visible rounded-[3px] border border-borderStrong/45 bg-parchment shadow-[inset_0_1px_0_rgb(255_255_255/0.7),inset_0_0_22px_rgb(139_106_52/0.1),0_1px_3px_rgb(2_16_12/0.55)]">
          <span
            aria-hidden="true"
            className="px-paper-grain pointer-events-none absolute inset-0 -z-10 rounded-[inherit] opacity-35 mix-blend-multiply"
          />
          <span
            aria-hidden="true"
            className="absolute -top-[6px] left-1/2 h-[10px] w-[10px] -translate-x-1/2 rotate-45 border border-brand bg-frame"
          />
          <GameStatGrid cells={game.statCells} />
          <GameStreakStats currentDays={game.streak.currentDays} bestDays={game.streak.bestDays} />
        </div>

        {/* Dark footing: a band of the frame left visible below the panel,
          closed by the same ruled diamond ornament the section headings use.

          Both ends of the band deepen into shadow, so the emerald reads as
          lit in the middle and the card's corners settle rather than ending
          on a flat field. Done as a gradient rather than as another raster
          strip: it rescales with the card at every breakpoint, needs no crop
          or asset pipeline, keeps the centre exactly the frame token the rest
          of the shell uses, and its strength is a number to tune here. */}
        <div
          aria-hidden="true"
          className="relative z-[3] flex justify-center bg-[linear-gradient(90deg,rgb(0_0_0/0.5)_0%,rgb(0_0_0/0.26)_8%,rgb(0_0_0/0.08)_18%,transparent_30%,transparent_70%,rgb(0_0_0/0.08)_82%,rgb(0_0_0/0.26)_92%,rgb(0_0_0/0.5)_100%)] px-3 py-[7px]"
        >
          <OrnamentalRule className="w-full max-w-[15rem]" />
        </div>
      </div>
    </GameCardFrame>
  );
}
